import { useMemo } from 'react';
import { ChatHostRuntimeProvider, useChatActions } from '@aimsos/chat';

/**
 * Inner component rendered **inside** `ChatProviderWrapper` so it has access to
 * `useChatActions()` from the shell's own `HomeChatProviderShell`.
 *
 * Extends the host runtime value with `openChatWithAgent(agent)` — a callback
 * that embedded apps (e.g. the Agents list) can call to open the shell assistant
 * panel pre-selected to a specific agent.
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {object} props.baseRuntime — apiClient, wsService, auth, notifications from ShellLayout
 * @param {(open: boolean) => void} props.setChatOpen — ShellLayout state setter
 * @param {(layout: 'split'|'fullscreen') => void} props.setChatLayout — ShellLayout state setter
 */
export function ShellChatRuntimeProvider({ children, baseRuntime, setChatOpen, setChatLayout }) {
  const { selectAgent, clearConversationSelection, cleanThread } = useChatActions();

  const value = useMemo(
    () => ({
      ...baseRuntime,
      openChatWithAgent: (agent) => {
        setChatOpen(true);
        setChatLayout('split');
        clearConversationSelection?.();
        cleanThread?.();
        if (agent?.id && selectAgent) {
          selectAgent(String(agent.id), {
            uuid: agent.id,
            id: agent.id,
            name: agent.name,
            description: agent.description,
            model: agent.model,
            metadata: {
              description: agent.description,
              model: agent.model,
            },
          });
        }
      },
    }),
    [baseRuntime, setChatOpen, setChatLayout, selectAgent, clearConversationSelection, cleanThread],
  );

  return <ChatHostRuntimeProvider value={value}>{children}</ChatHostRuntimeProvider>;
}
